'use client'

import type {
  IssueBillingKeyAndPayRequest,
  IssueBillingKeyAndPayResponse,
  IssueBillingKeyRequest,
  IssueBillingKeyResponse,
  PaymentRequest,
  PaymentResponse,
} from '@portone/browser-sdk/v2'
import * as PortOne from '@portone/browser-sdk/v2'
import { env } from '@shared/config/env'

export type { IssueBillingKeyRequest, IssueBillingKeyResponse, PaymentRequest, PaymentResponse }

/**
 * Eximbay V2의 빌링키 발급+초회결제 계약.
 * 현재 사용 중인 browser-sdk 타입은 Eximbay가 사용하지 않는
 * billingKeyAndPayMethod를 필수로 선언하므로, 공식 Eximbay 요청 계약을 이 경계에서만 보정한다.
 */
export type EximbayIssueBillingKeyAndPayRequest = {
  storeId: string
  channelKey: string
  paymentId: string
  orderName: string
  totalAmount: number
  currency: string
  locale?: string
  customer: {
    customerId: string
    fullName: string
    email: string
    phoneNumber?: string
  }
  customData: Record<string, unknown>
  noticeUrls?: string[]
  bypass?: {
    eximbay_v2?: {
      payment?: Record<string, unknown>
      settings?: Record<string, unknown>
      [key: string]: unknown
    }
    [key: string]: unknown
  }
  redirectUrl?: string
  forceRedirect?: boolean
  appScheme?: string
}

/** 배포 전 생성된 JP 튜터지정권 ONE_TIME 주문의 TTL 내 호환 요청. */
export type LegacyEximbayPaymentRequest = EximbayIssueBillingKeyAndPayRequest

export const normalizePortoneCurrency = (currency?: string): PaymentRequest['currency'] => {
  return (currency?.trim().toUpperCase() || 'KRW') as PaymentRequest['currency']
}

/**
 * PortOne V2 결제 요청 (일시불 결제용)
 * V1의 IMP.request_pay()를 대체
 */
export const requestPayment = async (params: Omit<PaymentRequest, 'storeId'>): Promise<PaymentResponse | undefined> => {
  return PortOne.requestPayment({
    storeId: env.NEXT_PUBLIC_PORTONE_STORE_ID,
    ...params,
  })
}

/**
 * PortOne V2 빌링키 발급 (정기결제 등록용)
 * 빌링키만 발급하고, 실제 결제는 서버에서 수행
 */
export const requestIssueBillingKey = async (
  params: Omit<IssueBillingKeyRequest, 'storeId'>,
): Promise<IssueBillingKeyResponse | undefined> => {
  return PortOne.requestIssueBillingKey({
    storeId: env.NEXT_PUBLIC_PORTONE_STORE_ID,
    ...params,
  })
}

/**
 * PortOne V2 빌링키 발급 + 초회 결제 (정기결제 등록 + 첫 결제 동시 진행)
 * KG이니시스/웰컴페이먼츠 휴대폰 결제 등 특정 PG에서 사용
 */
export const requestIssueBillingKeyAndPay = async (
  params: Omit<IssueBillingKeyAndPayRequest, 'storeId'>,
): Promise<IssueBillingKeyAndPayResponse | undefined> => {
  return PortOne.requestIssueBillingKeyAndPay({
    storeId: env.NEXT_PUBLIC_PORTONE_STORE_ID,
    ...params,
  })
}

export const requestEximbayBillingKeyAndPay = async (
  params: EximbayIssueBillingKeyAndPayRequest,
): Promise<IssueBillingKeyAndPayResponse | undefined> => {
  // 주문은 Redis에서 최대 72시간 재사용된다. 배포 전 주문에 남아 있는 폐기 필드나
  // 향후 서버 내부 필드가 PortOne SDK까지 전파되지 않도록 공식 Eximbay 계약만 전달한다.
  const {
    storeId,
    channelKey,
    paymentId,
    orderName,
    totalAmount,
    currency,
    customer,
    customData,
    locale,
    noticeUrls,
    bypass,
    redirectUrl,
    forceRedirect,
    appScheme,
  } = params

  return PortOne.requestIssueBillingKeyAndPay({
    storeId,
    channelKey,
    paymentId,
    orderName,
    totalAmount,
    currency,
    customer,
    customData,
    ...(locale === undefined ? {} : { locale }),
    ...(noticeUrls === undefined ? {} : { noticeUrls }),
    ...(bypass === undefined ? {} : { bypass }),
    ...(redirectUrl === undefined ? {} : { redirectUrl }),
    ...(forceRedirect === undefined ? {} : { forceRedirect }),
    ...(appScheme === undefined ? {} : { appScheme }),
  } as unknown as IssueBillingKeyAndPayRequest)
}

export const requestLegacyEximbayPayment = async (
  params: LegacyEximbayPaymentRequest,
): Promise<PaymentResponse | undefined> => {
  const {
    storeId,
    channelKey,
    paymentId,
    orderName,
    totalAmount,
    currency,
    customer,
    customData,
    locale,
    noticeUrls,
    bypass,
    redirectUrl,
    forceRedirect,
    appScheme,
  } = params

  return PortOne.requestPayment({
    storeId,
    channelKey,
    paymentId,
    orderName,
    totalAmount,
    currency,
    payMethod: 'CARD',
    customer,
    customData,
    ...(locale === undefined ? {} : { locale }),
    ...(noticeUrls === undefined ? {} : { noticeUrls }),
    ...(bypass === undefined ? {} : { bypass }),
    ...(redirectUrl === undefined ? {} : { redirectUrl }),
    ...(forceRedirect === undefined ? {} : { forceRedirect }),
    ...(appScheme === undefined ? {} : { appScheme }),
  } as PaymentRequest)
}

/**
 * V2 응답에서 에러 여부 확인
 * V2에서는 code가 undefined가 아니면 에러
 */
export const requestEximbayOneTimePayment = requestLegacyEximbayPayment

export const isPaymentError = (
  response: PaymentResponse | IssueBillingKeyResponse | IssueBillingKeyAndPayResponse | undefined,
): boolean => {
  return response?.code !== undefined
}
